import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Loader2 } from 'lucide-react';
import Lottie from 'lottie-react';
import { useAuthStore } from '@/stores/auth.store';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { getErrorMessage } from '@/lib/errors';
import { trackAdminAction } from '@/services/client-analytics';
import bubblesAnimation from '@/assets/animations/bubbles.json';

const loginSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
});

type LoginFormData = z.infer<typeof loginSchema>;

export function LoginPage() {
  const navigate = useNavigate();
  const signIn = useAuthStore(state => state.signIn);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    setError(null);

    try {
      void trackAdminAction('admin_login_attempt', { email: data.email });
      await signIn(data);
      void trackAdminAction('admin_login_success', { email: data.email });
      navigate('/dashboard');
    } catch (error) {
      void trackAdminAction('admin_login_failed', {
        email: data.email,
        reason: getErrorMessage(error),
      });
      setError(getErrorMessage(error, 'Failed to sign in'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#3366ff]">
      <div className="pointer-events-none absolute inset-0 z-0 opacity-100 [filter:drop-shadow(0_0_10px_rgba(255,255,255,0.25))]">
        <Lottie
          animationData={bubblesAnimation}
          loop
          autoplay
          rendererSettings={{ preserveAspectRatio: 'xMidYMid slice' }}
          style={{ width: '100%', height: '100%' }}
        />
      </div>

      <div className="relative z-10 flex min-h-screen items-center justify-center px-4 py-10 sm:px-6">
        <Card className="w-full max-w-md border-white/20 bg-white/95 shadow-2xl backdrop-blur-xl">
          <CardHeader className="space-y-1">
            <img src="/bevpass-logo.svg" alt="BevPass" className="mx-auto mb-2 h-10 w-auto" />
            <CardTitle className="text-2xl font-bold text-center">
              BevPass Admin
            </CardTitle>
            <CardDescription className="text-center">
              Sign in to your admin account
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  {...register('email')}
                  type="email"
                  id="email"
                  placeholder="admin@bevpass.com"
                  disabled={isLoading}
                  autoComplete="email"
                />
                {errors.email && (
                  <p className="text-sm text-red-600">{errors.email.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  {...register('password')}
                  type="password"
                  id="password"
                  placeholder="••••••••"
                  disabled={isLoading}
                  autoComplete="current-password"
                />
                {errors.password && (
                  <p className="text-sm text-red-600">{errors.password.message}</p>
                )}
              </div>

              {error && (
                <div className="rounded-md border border-red-200 bg-red-50 p-3">
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}

              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  'Sign In'
                )}
              </Button>

              <div className="mt-4 space-y-2 text-center">
                <p className="text-sm text-gray-600">
                  New to BevPass?{' '}
                  <a href="/auth/register" className="font-medium text-primary hover:underline">
                    Register for access
                  </a>
                </p>
                <p className="text-xs text-gray-500">
                  Already have an invitation? Check your email for a sign-in link.
                </p>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default LoginPage;
